/**
 * Nested “four pillars” navigation for League Admin — desktop rail + mobile sheet body.
 */
import { Link, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  ChevronDown,
  CloudRain,
  Grid3X3,
  LayoutGrid,
  Shirt,
  Users,
  Wallet,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

type LeagueProgramDivision = {
  id: string;
  name: string;
  registrationType: string;
};

type LeagueTeamBrief = {
  id: string;
  name: string;
  slug: string;
  registrationType: string;
};

type LeaguePillarNavProps = {
  organizationId: string | null;
  onNavigate?: () => void;
};

function NavLink({
  href,
  children,
  active,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <Link href={href} className="block w-full min-h-11 touch-manipulation">
      <div
        onClick={onClick}
        className={cn(
          'rounded-md px-2.5 py-2.5 min-h-11 flex items-center text-[11px] font-broadcast uppercase tracking-wider transition-colors cursor-pointer active:scale-[0.98]',
          active
            ? 'bg-[#D4AF37]/15 text-[#D4AF37]'
            : 'text-white/65 hover:text-white hover:bg-white/5'
        )}
      >
        {children}
      </div>
    </Link>
  );
}

function PillarHeader({ icon: Icon, title }: { icon: typeof Shirt; title: string }) {
  return (
    <div className="flex items-center gap-2 px-1 py-1 text-[10px] font-broadcast uppercase tracking-[0.2em] text-white/35">
      <Icon className="h-3.5 w-3.5 text-[#D4AF37]/70" />
      {title}
    </div>
  );
}

export function LeaguePillarNav({ organizationId, onNavigate }: LeaguePillarNavProps) {
  const [location] = useLocation();
  const path = location.split('?')[0];

  const { data: divisions = [] } = useQuery<LeagueProgramDivision[]>({
    queryKey: ['/api/league/divisions', organizationId],
    queryFn: async () => {
      const url = `/api/league/divisions?organizationId=${encodeURIComponent(organizationId!)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch divisions');
      return res.json();
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });

  const { data: teams = [] } = useQuery<LeagueTeamBrief[]>({
    queryKey: ['/api/league/teams', organizationId, 'nav'],
    queryFn: async () => {
      const url = `/api/league/teams?organizationId=${encodeURIComponent(organizationId!)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch teams');
      return res.json();
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });

  const houseDivisions = divisions.filter(d => d.registrationType === 'HOUSELEAGUE');
  const selectTeams = teams.filter(t => t.registrationType === 'SELECT');
  const orgPath = organizationId ?? '';

  const handleNav = () => onNavigate?.();

  return (
    <nav className="flex flex-col gap-1 py-4 px-3" aria-label="League command pillars">
      <div className="mb-3 px-1">
        <p className="text-[9px] font-broadcast uppercase tracking-[0.35em] text-[#D4AF37]/60">
          Holistic Command
        </p>
        <p className="text-sm font-bold text-white uppercase tracking-tight mt-0.5">League Ops</p>
      </div>

      <Collapsible defaultOpen className="group space-y-0.5">
        <CollapsibleTrigger className="flex w-full min-h-11 items-center justify-between rounded-md px-1 py-2 text-left hover:bg-white/5 touch-manipulation active:scale-[0.98]">
          <PillarHeader icon={Shirt} title="League logistics" />
          <ChevronDown className="h-4 w-4 shrink-0 text-white/30 transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-0.5 pl-1 border-l border-white/10 ml-2">
          <NavLink
            href="/admin/league/uniforms"
            active={path === '/admin/league/uniforms' || path === '/admin/league/uniform-manifest'}
            onClick={handleNav}
          >
            Master uniform manifest
          </NavLink>
          <NavLink
            href="/admin/league/compliance"
            active={path === '/admin/league/compliance'}
            onClick={handleNav}
          >
            Compliance vault
          </NavLink>
          <NavLink
            href="/admin/league#import-ops"
            active={path === '/admin/league'}
            onClick={handleNav}
          >
            Data import &amp; OBA (home)
          </NavLink>
        </CollapsibleContent>
      </Collapsible>

      <Collapsible defaultOpen className="group space-y-0.5">
        <CollapsibleTrigger className="flex w-full min-h-11 items-center justify-between rounded-md px-1 py-2 text-left hover:bg-white/5 touch-manipulation active:scale-[0.98]">
          <PillarHeader icon={LayoutGrid} title="Field operations" />
          <ChevronDown className="h-4 w-4 shrink-0 text-white/30 transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-0.5 pl-1 border-l border-white/10 ml-2">
          <NavLink
            href="/admin/league/master-scheduler"
            active={path === '/admin/league/master-scheduler'}
            onClick={handleNav}
          >
            Master scheduler
          </NavLink>
          <NavLink
            href="/admin/league/ai-sandbox"
            active={path === '/admin/league/ai-sandbox'}
            onClick={handleNav}
          >
            AI Sandbox (Beta)
          </NavLink>
          <NavLink
            href="/admin/league/master-scheduler"
            active={path === '/admin/league/master-scheduler'}
            onClick={handleNav}
          >
            <span className="inline-flex items-center gap-1.5">
              <CloudRain className="h-3 w-3 opacity-70" />
              Rainout command
            </span>
          </NavLink>
        </CollapsibleContent>
      </Collapsible>

      <Collapsible defaultOpen className="group space-y-0.5">
        <CollapsibleTrigger className="flex w-full min-h-11 items-center justify-between rounded-md px-1 py-2 text-left hover:bg-white/5 touch-manipulation active:scale-[0.98]">
          <PillarHeader icon={Users} title="People &amp; rosters" />
          <ChevronDown className="h-4 w-4 shrink-0 text-white/30 transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1 pl-1 border-l border-white/10 ml-2 max-h-[40vh] overflow-y-auto">
          <NavLink href="/admin/league" active={path === '/admin/league'} onClick={handleNav}>
            Command center home
          </NavLink>
          <NavLink
            href="/admin/org/forest-glade/staff"
            active={path.startsWith('/admin/org/forest-glade/staff')}
            onClick={handleNav}
          >
            Staff manager
          </NavLink>
          {houseDivisions.map(div => (
            <div key={div.id} className="space-y-0.5">
              <p className="px-2.5 pt-1 text-[10px] text-white/40 truncate" title={div.name}>
                {div.name}
              </p>
              <NavLink
                href={`/admin/league/draft/${div.id}`}
                active={path === `/admin/league/draft/${div.id}`}
                onClick={handleNav}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Grid3X3 className="h-3 w-3 opacity-70" />
                  Draft board
                </span>
              </NavLink>
              <NavLink
                href={`/admin/league/division/${div.id}`}
                active={path === `/admin/league/division/${div.id}`}
                onClick={handleNav}
              >
                Division war room
              </NavLink>
            </div>
          ))}
          {selectTeams.length > 0 && (
            <div className="pt-1">
              <p className="px-2.5 text-[10px] text-white/40 uppercase tracking-wider">
                Select teams
              </p>
              {selectTeams.map(t => (
                <NavLink
                  key={t.id}
                  href={`/admin/league/${t.slug}`}
                  active={path === `/admin/league/${t.slug}`}
                  onClick={handleNav}
                >
                  <span className="truncate">{t.name}</span>
                </NavLink>
              ))}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      <Collapsible defaultOpen className="group space-y-0.5">
        <CollapsibleTrigger className="flex w-full min-h-11 items-center justify-between rounded-md px-1 py-2 text-left hover:bg-white/5 touch-manipulation active:scale-[0.98]">
          <PillarHeader icon={Wallet} title="Financials" />
          <ChevronDown className="h-4 w-4 shrink-0 text-white/30 transition-transform group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-0.5 pl-1 border-l border-white/10 ml-2">
          <NavLink
            href="/admin/league/financials"
            active={path === '/admin/league/financials'}
            onClick={handleNav}
          >
            Financial command center
          </NavLink>
          <NavLink href={`/booking/${orgPath}/reports`} onClick={handleNav}>
            <span className="inline-flex items-center gap-1.5">
              <Building2 className="h-3 w-3 opacity-70" />
              Registration revenue
            </span>
          </NavLink>
          <NavLink href={`/booking/${orgPath}/stripe-settings`} onClick={handleNav}>
            Stripe payouts
          </NavLink>
        </CollapsibleContent>
      </Collapsible>
    </nav>
  );
}
